import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useFavourites } from '@/store/favourites';
import { useDashboardMode } from '@/store/mode';

/* Native-app bottom tab bar. Shown on phones/tablets (< lg); the desktop
   top nav takes over at lg and up. Lives inside the fixed layer so it stays
   put while the page scrolls, and honours the home-indicator safe area. */

const tab = ({ isActive }: { isActive: boolean }) =>
  `relative flex flex-1 flex-col items-center justify-center gap-[3px] h-full text-[10px] font-medium tracking-wide transition-colors ${
    isActive ? 'text-gold-dark' : 'text-mute'
  }`;

const ic = 'w-[22px] h-[22px]';

export default function BottomNav() {
  const user = useAuthStore((s) => s.user);
  const favCount = useFavourites((s) => s.items.length);
  const navigate = useNavigate();
  const setMode = useDashboardMode((s) => s.setMode);

  // The centre button flips the app into Selling and opens the seller hub
  // (where 'Sell new item' lives) rather than jumping straight to the form.
  const goSell = () => {
    if (user?.id) setMode('SELLER', user.id);
    navigate(user ? '/dashboard' : '/login');
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-cream/95 backdrop-blur-xl border-t border-warmline"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Primary"
    >
      <div className="flex items-stretch h-[var(--bottom-nav-h)] max-w-xl mx-auto px-1">
        <NavLink to="/" end className={tab} aria-label="Home">
          <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/></svg>
          <span>Home</span>
        </NavLink>

        <NavLink to="/c/all" className={tab} aria-label="Search">
          <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg>
          <span>Search</span>
        </NavLink>

        {/* Centre button - switches to Selling and opens the seller hub */}
        <button type="button" onClick={goSell} className="flex flex-1 items-start justify-center" aria-label="Sell - go to your selling dashboard">
          <span className="-mt-5 grid place-items-center w-14 h-14 rounded-full ring-4 ring-cream shadow-glow bg-gold text-ink active:bg-gold-dark transition-colors">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
          </span>
        </button>

        <NavLink to="/saved" className={tab} aria-label="Saved">
          <span className="relative">
            <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-4.5-9.3-9.2C1 8.4 3.4 5 6.7 5c2 0 3.4 1.1 4.3 2.5C12 6.1 13.4 5 15.4 5c3.3 0 5.7 3.4 4 6.8C19 16.5 12 21 12 21z"/></svg>
            {favCount > 0 && (
              <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-1 bg-gold text-ink text-[9px] font-bold rounded-full grid place-items-center">{favCount > 9 ? '9+' : favCount}</span>
            )}
          </span>
          <span>Saved</span>
        </NavLink>

        <NavLink to={user ? '/dashboard' : '/login'} className={tab} aria-label="Account">
          <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.4"/><path d="M5 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5"/></svg>
          <span>Account</span>
        </NavLink>
      </div>
    </nav>
  );
}
